/* PureEatiz header: mobile drawer, sticky state, dropdown accessibility */
if (!customElements.get('pe-header')) {
  class PeHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.pe-drawer');
      this.openBtn = this.querySelector('[data-pe-drawer-open]');

      this.openBtn?.addEventListener('click', this.openDrawer);
      this.querySelector('[data-pe-drawer-close]')?.addEventListener('click', this.closeDrawer);
      this.drawer?.addEventListener('click', this.onBackdropClick);
      this.drawer?.addEventListener('cancel', this.onCancel);

      this.parents = [...this.querySelectorAll('.pe-nav__item--parent')];
      this.parents.forEach((item) => {
        item.addEventListener('mouseenter', () => this.setExpanded(item, true));
        item.addEventListener('mouseleave', () => this.setExpanded(item, false));
        item.addEventListener('focusin', () => this.setExpanded(item, true));
        item.addEventListener('focusout', (event) => {
          if (!item.contains(/** @type {Node} */ (event.relatedTarget))) this.setExpanded(item, false);
        });
      });
      document.addEventListener('keydown', this.onKeydown);

      // Horizon scrolls `.page-wrapper` on desktop (>= 990px) and the window on mobile.
      this.scroller = document.querySelector('.page-wrapper');
      // Measure on the next frame / in the observer's first callback (after layout), not synchronously during load.
      requestAnimationFrame(() => this.onScroll());
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller?.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateHeights);
        this.resizeObserver.observe(this);
      } else {
        this.updateHeights();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.scroller?.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      this.resizeObserver?.disconnect();
      document.documentElement.style.removeProperty('overflow');
    }

    setExpanded(item, open) {
      item.classList.toggle('is-open', open);
      item.classList.remove('is-closed');
      item.querySelector(':scope > .pe-nav__link')?.setAttribute('aria-expanded', String(open));
    }

    onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const open = this.parents.find((item) => item.classList.contains('is-open'));
      if (!open) return;
      this.setExpanded(open, false);
      open.classList.add('is-closed');
      /** @type {HTMLElement | null} */ (open.querySelector(':scope > .pe-nav__link'))?.focus();
    };

    onScroll = () => {
      const offset = Math.max(window.scrollY, this.scroller?.scrollTop ?? 0);
      this.classList.toggle('is-scrolled', offset > 10);
    };

    updateHeights = () => {
      const height = this.querySelector('.pe-header__bar')?.getBoundingClientRect().height ?? 0;
      const group = document.getElementById('header-group');
      document.body.style.setProperty('--header-height', `${Math.round(height)}px`);
      if (group) {
        // #header-group is `display: contents` when sticky, so sum its children instead.
        const groupHeight = [...group.children].reduce(
          (total, el) => total + (el instanceof HTMLElement ? el.offsetHeight : 0),
          0
        );
        document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
      }
    };

    openDrawer = () => {
      if (!this.drawer) return;
      this.drawer.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.openBtn?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.drawer.classList.add('is-visible'));
    };

    closeDrawer = () => {
      if (!this.drawer?.open) return;
      this.drawer.classList.remove('is-visible');
      this.openBtn?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.removeProperty('overflow');
      const panel = this.drawer.querySelector('.pe-drawer__panel');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!panel || reduced) {
        this.drawer.close();
        return;
      }
      panel.addEventListener('transitionend', () => this.drawer.close(), { once: true });
    };

    onCancel = (event) => {
      event.preventDefault();
      this.closeDrawer();
    };

    onBackdropClick = (event) => {
      if (event.target === this.drawer) this.closeDrawer();
    };
  }

  customElements.define('pe-header', PeHeader);
}
